/**
 * Stripe payment form.
 *
 * Renders inside <Elements>. Shows Apple Pay / Google Pay buttons (when the
 * shopper's device offers them) above the card fields. Card data is entered in
 * Stripe-hosted iframes, so it never touches our servers.
 */
import React, { useState } from 'react';
import {
  PaymentElement,
  ExpressCheckoutElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

function StripePaymentForm({
  total,
  disabled,
  onBeforePay,
  onPaid,
  onError,
  returnUrl,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [isPaying, setIsPaying] = useState(false);
  const [message, setMessage] = useState('');
  const [hasExpressOptions, setHasExpressOptions] = useState(false);

  const pay = async (confirmFn) => {
    if (!stripe || !elements) return;

    setMessage('');
    setIsPaying(true);

    try {
      // Let the checkout page attach the latest customer details to the intent
      // before the charge goes through.
      if (onBeforePay) {
        const ok = await onBeforePay();
        if (!ok) {
          setIsPaying(false);
          return;
        }
      }

      const { error, paymentIntent } = await confirmFn();

      if (error) {
        // card_error / validation_error are safe to show the shopper as-is.
        const text = error.message || 'Payment could not be completed. Please try again.';
        setMessage(text);
        if (onError) onError(error);
        setIsPaying(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        await onPaid(paymentIntent);
      } else if (paymentIntent) {
        setMessage(`Payment status: ${paymentIntent.status}. We'll email you once it clears.`);
      }
    } catch (err) {
      setMessage(err.message || 'Something went wrong while taking payment.');
      if (onError) onError(err);
    } finally {
      setIsPaying(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    pay(() =>
      stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
        // Stay on the page unless the bank demands a redirect (3-D Secure).
        redirect: 'if_required',
      })
    );
  };

  const handleExpressConfirm = () => {
    pay(() =>
      stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
        redirect: 'if_required',
      })
    );
  };

  return (
    <form className="stripePaymentForm" onSubmit={handleSubmit}>
      {/* Apple Pay / Google Pay - only renders if the device supports one. */}
      <ExpressCheckoutElement
        options={{ buttonHeight: 44 }}
        onReady={({ availablePaymentMethods }) =>
          setHasExpressOptions(Boolean(availablePaymentMethods))
        }
        onConfirm={handleExpressConfirm}
        onCancel={() => setIsPaying(false)}
      />

      {hasExpressOptions && (
        <div className="stripePayDivider"><span>or pay by card</span></div>
      )}

      <PaymentElement options={{ layout: 'tabs' }} />

      {message && <p className="stripePayError">{message}</p>}

      <button
        type="submit"
        className={`payNowBtn ${disabled || isPaying || !stripe ? 'disabled' : ''}`}
        disabled={disabled || isPaying || !stripe}
      >
        {isPaying ? 'PROCESSING…' : `PAY £${Number(total || 0).toFixed(2)}`}
      </button>

      <p className="stripePayNote">Secured by Stripe · We never store your card details</p>
    </form>
  );
}

export default StripePaymentForm;
